import { openModal } from "./modal";
import { loadMenuSettings, saveMenuSettings } from "./menuSettings";
import { loadGraphicsQuality, saveGraphicsQuality } from "./graphicsSettings";
import { MOVE_HINTS_ICON_SVG, TAVERNEU_ICON_SVG, GRAPHICS_ICON_SVG } from "./cornerIcons";
import { clearCacheAndReload } from "./cacheUtils";
import type { GraphicsQuality } from "../render/scene";

export interface OptionsModalCallbacks {
  /** Repercute le changement sur la partie en cours (absent depuis le menu, ou rien a mettre a jour). */
  onMoveHintsChange?: (enabled: boolean) => void;
  onTaverneuCommentaryChange?: (enabled: boolean) => void;
  /** Applique le nouveau niveau de rendu a la scene 3D en cours, immediatement (absent depuis le menu, avant toute partie). */
  onGraphicsQualityChange?: (quality: GraphicsQuality) => void;
}

const GRAPHICS_QUALITY_LABELS: Record<GraphicsQuality, string> = {
  high: "Elevee",
  standard: "Standard",
  battery: "Economie",
};

function optionRowHtml(id: string, icon: string, title: string, checked: boolean): string {
  return `
    <label class="option-row" for="${id}">
      <span class="option-icon">${icon}</span>
      <span class="option-title">${title}</span>
      <span class="option-checkbox">
        <input type="checkbox" id="${id}" ${checked ? "checked" : ""} />
        <span class="option-checkbox-box"></span>
      </span>
    </label>`;
}

/**
 * Reglage de qualite graphique : pas une simple bascule (trois niveaux), donc un
 * groupe segmente plutot qu'une case a cocher — meme widget que Humain/Bot dans le
 * menu de demarrage. Stocke a part du reste des reglages (cf. `graphicsSettings.ts`) :
 * partage entre tous les jeux du Taverneux, et applique immediatement (cf. `setQuality`
 * de la scene 3D), y compris en cours de partie.
 */
function graphicsQualityRowHtml(current: GraphicsQuality): string {
  const buttons = (Object.keys(GRAPHICS_QUALITY_LABELS) as GraphicsQuality[])
    .map(
      (quality) =>
        `<button type="button" data-quality="${quality}" class="${quality === current ? "active" : ""}">${GRAPHICS_QUALITY_LABELS[quality]}</button>`,
    )
    .join("");
  return `
    <div class="option-row option-row-stacked">
      <span class="option-icon">${GRAPHICS_ICON_SVG}</span>
      <span class="option-title">Qualite graphique</span>
    </div>
    <div class="segmented graphics-quality-toggle">${buttons}</div>`;
}

/**
 * Modale Options, accessible depuis le menu sandwich (menu de demarrage et HUD en
 * jeu). Chaque bascule persiste immediatement (via `menuSettings`/`graphicsSettings`)
 * et, si fournis, appelle le callback correspondant pour repercuter le changement sur
 * la partie en cours sans attendre un rechargement.
 */
export function showOptionsModal(callbacks: OptionsModalCallbacks = {}): void {
  const settings = loadMenuSettings();
  const content = document.createElement("div");
  content.innerHTML = `
    <h2 class="title-display">Options</h2>
    <div class="modal-scroll">
      ${optionRowHtml("opt-hints-checkbox", MOVE_HINTS_ICON_SVG, "Aide : previsualisation des deplacements", settings.showMoveHints)}
      ${optionRowHtml("opt-taverneu-checkbox", TAVERNEU_ICON_SVG, "Le Taverneu commente la partie", settings.taverneuCommentary)}
      ${graphicsQualityRowHtml(loadGraphicsQuality())}
      <div class="field">
        <label>Mise a jour</label>
        <button type="button" class="btn btn-ghost" id="opt-clear-cache-btn">Vider le cache et actualiser</button>
      </div>
    </div>
    <div class="menu-actions">
      <button class="btn" id="close-options" type="button">Fermer</button>
    </div>
  `;
  const { close } = openModal(content);
  content.querySelector<HTMLButtonElement>("#close-options")?.addEventListener("click", close);

  content.querySelector<HTMLButtonElement>("#opt-clear-cache-btn")?.addEventListener("click", (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Nettoyage...";
    void clearCacheAndReload();
  });

  content.querySelector<HTMLInputElement>("#opt-hints-checkbox")?.addEventListener("change", (e) => {
    const enabled = (e.target as HTMLInputElement).checked;
    const s = loadMenuSettings();
    s.showMoveHints = enabled;
    saveMenuSettings(s);
    callbacks.onMoveHintsChange?.(enabled);
  });

  content.querySelector<HTMLInputElement>("#opt-taverneu-checkbox")?.addEventListener("change", (e) => {
    const enabled = (e.target as HTMLInputElement).checked;
    const s = loadMenuSettings();
    s.taverneuCommentary = enabled;
    saveMenuSettings(s);
    callbacks.onTaverneuCommentaryChange?.(enabled);
  });

  const qualityButtons = content.querySelectorAll<HTMLButtonElement>(".graphics-quality-toggle button");
  qualityButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const quality = button.dataset.quality as GraphicsQuality;
      saveGraphicsQuality(quality);
      qualityButtons.forEach((b) => b.classList.toggle("active", b === button));
      callbacks.onGraphicsQualityChange?.(quality);
    });
  });
}
